'use client';

import { useEffect, useRef, type RefObject } from 'react';

/** The reader asked for less motion: a surface does not move on its own. */
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Calls `onInView` while the element `ref` holds is in the viewport
 * (IntersectionObserver): once when it enters, and again whenever `enabled`
 * turns back on while it is still there, since observing anew reports where
 * the element is. What the kit's `LoadMore` uses to load the rest of a list
 * where the reader reaches its end.
 *
 * Off while `enabled` is false, without IntersectionObserver, and when the
 * reader asked for less motion (`prefers-reduced-motion: reduce`): content
 * that arrives by itself is motion the reader did not start, so the surface
 * keeps a control that does the same on a press.
 */
export function useInView(
  ref: RefObject<Element | null>,
  onInView: () => void,
  enabled: boolean,
): void {
  // The latest callback, read when the observer fires, so a new closure on
  // every render does not observe the element anew.
  const latest = useRef(onInView);
  useEffect(() => {
    latest.current = onInView;
  });
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia?.(REDUCED_MOTION_QUERY).matches) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) latest.current();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, enabled]);
}
